import {
    Component,
    OnInit,
    Input,
    Output,
    HostBinding,
    Inject,
    ElementRef,
    EventEmitter,
    ViewChild,
    forwardRef,
    AfterViewInit,
    OnDestroy,
    ChangeDetectorRef,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import type { IccInputType } from './input.interface';
import { MatCalendar, MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';

export const ICC_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputComponent),
    multi: true,
};

export const ICC_INPUT_VALIDATOR: any = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => InputComponent),
    multi: true,
};

const PHONE_NUMBER_REGEX = /^(\+)?[0-9(),;#-\s]*$/;

// eslint-disable-next-line
const EMAIL_REGEX = /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'icc-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    providers: [ICC_INPUT_CONTROL_VALUE_ACCESSOR, ICC_INPUT_VALIDATOR],
})
export class InputComponent implements OnInit, ControlValueAccessor, AfterViewInit, OnDestroy, Validator {

    private _disabled = false;
    private _placeholder: string | null = null;
    private _type: IccInputType = 'text';
    private _minLength: number | null = null;
    private _maxLength: number | null = null;
    private _min: number | string | null = null;
    private _max: number | string | null = null;
    private _step: number | string | null = null;
    private _value: string | number | Date = '';
    private _readonly = false;
    private _required = false;
    private _name: string | null = null;
    private _supportText: string | null = null;
    private _size: 'large' | 'medium' | 'small' = 'small';
    private _prefix: string | null = null;
    private _prefixIcon: string | null = null;
    private _suffix: string | null = null;
    private _suffixTitle: string | null = null;
    private _align: 'left' | 'center' | 'right' = 'left';
    private _datePickerSide: 'left' | 'right' = 'right';
    private _accept: string | null = null;
    private _rows: number | null = null;
    private _cols: number | null = null;
    private _phoneNumberErrorMessage: string | null = null;
    private _minLengthErrorMessage: string | null = null;
    private _pattern: string | null = null;
    private _dateErrorMessage: string | null = null;
    private _label: string | null = null;

    clearValueIsVisible = false;
    supportTextIsVisible = false;
    datePickerIsVisible = false;
    calendarIsVisible = false;

    focused = false;

    phoneNumberError = false;
    emailError = false;
    requiredError = false;
    minLengthError = false;

    lengthText = '';

    @ViewChild('datepicker') datepicker!: MatDatepicker<Date>;
    @ViewChild('input') input!: ElementRef<HTMLElement>;
    @ViewChild('inputDate', {
        read: MatDatepickerInput,
    }) inputDateComponent!: MatDatepickerInput<Date>;
    @ViewChild('inputDate') inputDate!: ElementRef<HTMLElement>;

    @Input()
    @HostBinding('class.icc-disabled')
    get disabled() {
        return this._disabled;
    }
    set disabled(value: any) {
        this._disabled = coerceBooleanProperty(value);
        this.handleElementsVisible();

    }
    @HostBinding('attr.disabled')
    get nativeDisabled(): boolean {
        return this.disabled;
    }

    @Input()
    @HostBinding('attr.placeholder')
    get placeholder(): string {
        return this._placeholder ? this._placeholder : '';
    }
    set placeholder(placeholder: string) {
        this._placeholder = placeholder ? placeholder : '';
    }

    @Input()
    @HostBinding('attr.type')
    get type(): IccInputType {
        return this._type;
    }
    set type(type: IccInputType) {
        this._type = type;
    }

    @Input()
    get minLength(): number | null {
        return this._minLength;
    }
    set minLength(minLength: number | null) {
        this._minLength = minLength;
    }

    @Input()
    get maxLength(): number | null {
        return this._maxLength;
    }
    set maxLength(maxLength: number | null) {
        this._maxLength = maxLength;
        this.handleElementsVisible();
    }

    @Input()
    get min(): number | string | null {
        return this._min;
    }
    set min(min: number | string | null) {
        this._min = min;
    }

    @Input()
    get max(): number | string |null {
        return this._max;
    }
    set max(max: number | string | null) {
        this._max = max;
    }

    @Input()
    get step(): number | string |null {
        return this._step;
    }
    set step(step: number | string | null) {
        this._step = step;
    }

    @Input()
    get value(): string | number | Date {
        return this._value;
    }
    set value(value: string | number  | Date) {
        if( this._value !== value){
            this._value = value;
            this.handleElementsVisible();
        }
    }

    @Input()
    @HostBinding('attr.readonly')
    get readonly(): boolean {
        return this._readonly;
    }
    set readonly(readonly: boolean) {
        this._readonly = readonly;
        this.handleElementsVisible();
    }

    @Input()
    @HostBinding('attr.required')
    get required(): boolean {
        return this._required;
    }
    set required(required: boolean) {
        this._required = required;
    }

    @Input()
    @HostBinding('attr.name')
    get name(): string | null {
        return this._name;
    }
    set name(name: string | null) {
        this._name = name;
    }

    @Input()
    get supportText(): string | null {
        return this._supportText;
    }
    set supportText(text: string | null) {
        this._supportText = text;
        this.handleElementsVisible();
    }

    @Input()
    get size() {
        return this._size;
    }
    set size(size: 'large' | 'medium' | 'small') {
        this._size = size;
    }

    @Input()
    get prefix(): string | null {
        return this._prefix;
    }
    set prefix(prefix: string | null) {
        this._prefix = prefix;
    }

    @Input()
    get prefixIcon(): string | null {
        return this._prefixIcon;
    }
    set prefixIcon(prefixIcon: string | null) {
        this._prefixIcon = prefixIcon;
    }

    @Input()
    get suffix(): string | null {
        return this._suffix;
    }
    set suffix(suffix: string | null) {
        this._suffix = suffix;
    }

    @Input()
    get suffixTitle(): string | null {
        return this._suffixTitle;
    }
    set suffixTitle(suffixTitle: string | null) {
        this._suffixTitle = suffixTitle;
    }

    @Input()
    get align(): 'left' | 'center' | 'right' {
        return this._align;
    }
    set align(align: 'left' | 'center' | 'right' ) {
        this._align = align;
    }

    @Input()
    get datePickerSide(): 'left' | 'right' {
        return this._datePickerSide;
    }
    set datePickerSide(datePickerSide: 'left' | 'right' ) {
        this._datePickerSide = datePickerSide;
    }

    @Input()
    get accept(): string | null {
        return this._accept;
    }
    set accept(accept: string | null) {
        this._accept = accept;
    }

    @Input()
    get rows(): number | null {
        return this._rows;
    }
    set rows(rows: number | null) {
        this._rows = rows;
    }

    @Input()
    get cols(): number | null {
        return this._cols;
    }
    set cols(cols: number | null) {
        this._cols = cols;
    }

    @Input()
    get pattern(): string | null {
        return this._pattern;
    }
    set pattern(pattern: string | null) {
        this._pattern = pattern;
    }

    @Input()
    get phoneNumberErrorMessage(): string | null {
        return this._phoneNumberErrorMessage;
    }
    set phoneNumberErrorMessage(phoneNumberErrorMessage: string | null) {
        this._phoneNumberErrorMessage = phoneNumberErrorMessage;
    }

    @Input()
    get minLengthErrorMessage(): string | null {
        return this._minLengthErrorMessage;
    }
    set minLengthErrorMessage(minLengthErrorMessage: string | null) {
        this._minLengthErrorMessage = minLengthErrorMessage
    }

    @Input()
    get dateErrorMessage(): string | null {
        return this._dateErrorMessage;
    }
    set dateErrorMessage(dateErrorMessage: string | null) {
        this._dateErrorMessage = dateErrorMessage;
    }

    @Input()
    get label(): string | null {
        return this._label;
    }
    set label(label: string | null) {
        this._label = label;
    }

    @Input() inputmode: string | null = null;
    @Input() autocomplete: string | null = null;

    @Input() calendarPopover = false;

    @Input() phoneNumberValidation = false;

    @Input() emailValidation = false;

    @Input() inputLengthIsVisible = false;

    @Input() requiredErrorMessage: string | null = null;

    @Input() errorMessage: string | null = null;

    @Output()
    readonly valueChange = new EventEmitter<string | number | Date | FileList>();

    @Output()
    readonly blurChange = new EventEmitter<boolean>();

    @Output()
    readonly focusedChange = new EventEmitter<boolean>();

    @Output()
    readonly hoveredChange = new EventEmitter<boolean>();

     // eslint-disable-next-line @typescript-eslint/no-empty-function
     _onTouched: () => any = () => {};
     // eslint-disable-next-line @typescript-eslint/no-empty-function
     private _controlValueAccessorChangeFn: (value: any) => void = () => {};

    constructor(
        private cd: ChangeDetectorRef,
        @Inject(ElementRef)
            private readonly _elementRef: ElementRef<HTMLElement>,
            private _focusMonitor: FocusMonitor,
        ) {}

    ngOnInit() {
        this.requiredError = this._required;
        this.handleElementsVisible();
        this.handleSize();
        this.handleClasses();
    }

    ngAfterViewInit(){
        if(this.inputDateComponent){
            this.inputDateComponent.dateChange.subscribe((date:Date) => {
                this.valueChange.emit(this.value);
            });
        }

        this.cd.detectChanges();

        this._focusMonitor.monitor(this._elementRef, true).subscribe((focusOrigin: FocusOrigin) => {
            if (!focusOrigin) {
                // When a focused element becomes disabled, the browser *immediately* fires a blur event.
                // Angular does not expect events to be raised during change detection, so any state change
                // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
                // See https://github.com/angular/angular/issues/17793. To work around this, we defer
                // telling the form control it has been touched until the next tick.
                Promise.resolve().then(() => {
                    this._onTouched();
                });
            }
        });
        if (this.required && !this._value) {
            this.requiredError = true;
        } else {
            this.requiredError = false;
        }
    }

    ngOnDestroy(){
        if(this.inputDateComponent?.dateChange){
            this.inputDateComponent.dateChange.unsubscribe();
        }
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    /** Focuses the checkbox. */
    focus(origin?: FocusOrigin, options?: FocusOptions): void {
        const input = this.input
            ? this.input
            : this.inputDate
                ? this.inputDate
                : null;
        if(input){
            if (origin) {
                this._focusMonitor.focusVia(input, origin, options);
            } else {
                input.nativeElement.focus(options);
            }
        }
    }

    writeValue(value: any): void {
        this.value = value;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    registerOnChange(fn: (_: any) => {}): void {
        this._controlValueAccessorChangeFn = fn;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    protected handleSize() {
        if (this._elementRef?.nativeElement?.classList?.value) {
            const classes = this._elementRef?.nativeElement?.classList;
            if (classes.value.includes('icc-size-large')) {
                this._size = 'large';
            }
            if (classes.value.includes('icc-size-medium')) {
                this._size = 'medium';
            }
            if (classes.value.includes('icc-size-small')) {
                this._size = 'small';
            }
        }
    }

    protected handleClasses() {
        if (this._elementRef?.nativeElement) {
            const element = this._elementRef.nativeElement as HTMLElement;

            element.classList.add('icc-size-' + this._size);

            if (this._disabled) {
                element.classList.add('icc-disabled');
            }

            if (this._readonly) {
                element.classList.add('icc-readonly');
            }

            if (this._align) {
                element.classList.add('icc-text-align-'+this._align);
            }

            if (element?.parentElement?.classList?.value) {
                const classes = element.parentElement.classList;
                if (classes.value.includes('icc-form-field-frame')) {
                    element.classList.add('in-form-field');
                }
                if (classes.value.includes('has-icon')) {
                    element.classList.add('parent-has-icon');
                }
            }

            if(this._type === 'date'){
                element.classList.add('icc-input-date');
            }
        }
    }

    protected handleElementsVisible() {
        if (
            this._maxLength &&
            ['text','email','password','textarea'].includes(this._type) &&
            typeof this.value === 'string' &&
            !this._disabled &&
            !this._readonly &&
            this.inputLengthIsVisible
        ) {
            this.handleLengthText();
        }

        this.supportTextIsVisible = this._supportText ? true : false;
        this.datePickerIsVisible = this._type === 'date';

        if (
            ['text','email','password','textarea'].includes(this._type) &&
            typeof this.value === 'string' &&
            this.value.length &&
            !this._disabled &&
            !this._readonly
        ){
            this.clearValueIsVisible = true;
        }else{
            this.clearValueIsVisible = false;
        }

    }

    protected handleLengthText() {
        if (this.inputLengthIsVisible && typeof this.value === 'string' && this.maxLength) {
            this.lengthText = this.value.length + '/' + this.maxLength;
        } else {
            this.lengthText = '';
        }
    }

    clearValue() {
        this._value = '';
        this.handleChange();
    }

    onChange(event: Event){
        event.stopPropagation();
        this._controlValueAccessorChangeFn(this._value);
        this.handleChange();
    }

    onClick(event: Event){
        if (this._type === "date") {
            this.switchCalendar();
        }
        event.stopPropagation();
    }

    handleChange() {
        if (this.required && !this._value) {
            this.requiredError = true;
        } else {
            this.requiredError = false;
        }
        if (this.phoneNumberValidation && this.type === 'text' && !this.validatePhoneNumber(this._value as string)) {
            this.phoneNumberError = true;
        } else {
            this.phoneNumberError = false;
        }
        if (this.emailValidation && this.type === 'email' && !this.validateEmail(this._value as string)) {
            this.emailError = true;
        } else {
            this.emailError = false;
        }
        if (this.minLength && this._value && this._value.toString().length < this.minLength) {
            this.minLengthError = true;
        } else {
            this.minLengthError = false;
        }
        this.handleElementsVisible();
        this.handleLengthText();
        if (this._type === 'file') {
            this.valueChange.emit(((this.input as ElementRef).nativeElement.control as HTMLInputElement).files);
        } else {
            this.valueChange.emit(this._value);
        }
    }

    handleBlur() {
        this.blurChange.emit(true);
    }

    handleFocused(focused: boolean) {
        this.focused = focused;
        this._onTouched();
        this.focusedChange.emit(focused);
    }

    switchCalendar(){
        this.datepicker.open();
    }

    validatePhoneNumber(value: string): boolean {
        return PHONE_NUMBER_REGEX.test(value);
    }

    validateEmail(value: string): boolean {
        return EMAIL_REGEX.test(value);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this?.input?.nativeElement) {
            if (this.phoneNumberValidation && !this.validatePhoneNumber(control.value)) {
                this.input.nativeElement.classList.add('ng-invalid');
                this.input.nativeElement.classList.remove('ng-valid');
                return { phoneNumberInvalid: true };
            } else if (this.emailValidation && !this.validateEmail(control.value)) {
                this.input.nativeElement.classList.add('ng-invalid');
                this.input.nativeElement.classList.remove('ng-valid');
                return { emailInvalid: true };
            }
            this.input.nativeElement.classList.add('ng-valid');
            this.input.nativeElement.classList.remove('ng-invalid');
        }
        return null;
    }

    registerOnValidatorChange?(fn: () => void): void {
        this._onTouched = fn;
    }
}
